/**
 * Shared album / artist thumbnails — the gradient + initials fallback with the
 * REAL cover (album) or photo (artist) layered over it.
 *
 * ONE implementation for every row / rail surface (home, search, queue, stats)
 * so covers appear consistently wherever a thumbnail does, instead of each view
 * hand-rolling a gradient-only `.sm-thumb`. The image is a positioned
 * `.sm-thumb__img` painted over the gradient (the SAME approach as the album
 * tiles' `.sm-cover__art-img` in primitives/Card.js and the artist grid's thumb
 * in views/browse/ArtistsList.js); it is built ONLY when the entity has real
 * art ({@link albumArtUrl} / {@link artistImageUrl} yield a URL), and on a load
 * error it removes itself, revealing the gradient + initials underneath — so a
 * broken / 404 image never leaves a row worse off than the art-less look.
 *
 * @module
 */
import { append, el } from './primitives/dom.js';
import { albumArtUrl, artistImageUrl, gradientFor, initialsOf } from './primitives/format.js';

/**
 * Layer a self-removing cover `<img>` over a gradient thumb. No-op when `url`
 * is undefined (the entity has no real art — the gradient stands).
 *
 * @param {HTMLElement} thumb
 * @param {string | undefined} url
 */
function layerImage(thumb, url) {
  if (url === undefined) return;
  const img = el('img', {
    className: 'sm-thumb__img',
    attrs: { src: url, alt: '', loading: 'lazy', decoding: 'async' },
    on: { error: () => img.remove() },
  });
  append(thumb, img);
}

/**
 * A square album thumbnail: gradient + initials with the real cover layered
 * over it when the album has `covers/…` art.
 *
 * @param {{ id: string, title: string, coverRef: string | null }} album
 * @param {string} [className] full class list for the thumb (default `sm-thumb`)
 * @returns {HTMLElement}
 */
export function albumThumb(album, className = 'sm-thumb') {
  const thumb = el('span', {
    className,
    text: initialsOf(album.title),
    attrs: { 'aria-hidden': 'true' },
    styleProps: { background: gradientFor(album.id) },
  });
  layerImage(thumb, albumArtUrl(album));
  return thumb;
}

/**
 * An artist thumbnail: gradient + initials with the real photo layered over it
 * when the artist has an `artists/…` ref.
 *
 * @param {{ id: string, name: string, imageRef: string | null }} artist
 * @param {string} [className] full class list for the thumb (default `sm-thumb`)
 * @returns {HTMLElement}
 */
export function artistThumb(artist, className = 'sm-thumb') {
  const thumb = el('span', {
    className,
    text: initialsOf(artist.name),
    attrs: { 'aria-hidden': 'true' },
    styleProps: { background: gradientFor(artist.id) },
  });
  layerImage(thumb, artistImageUrl(artist));
  return thumb;
}
